import { useState } from "react";
import { Slider } from "./ui/slider";
import { Card } from "./ui/card";
import { Clock, CalendarDays, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";

export const TimeSavedCalculator = () => {
  const [hoursPerClean, setHoursPerClean] = useState(3);
  const [frequency, setFrequency] = useState<"weekly" | "biweekly" | "monthly">("biweekly");

  const cleansPerYear = frequency === "weekly" ? 52 : frequency === "biweekly" ? 26 : 12;
  const hoursSavedYear = hoursPerClean * cleansPerYear;
  const daysSavedYear = Math.round((hoursSavedYear / 24) * 10) / 10;

  return (
    <section className="py-24 bg-muted/30">
      <div className="container px-4 md:px-6 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">How Much Time Could You Get Back?</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Calculate the hours you'll save by letting our professionals handle the cleaning.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <Card className="p-8 border-2 border-primary/20 shadow-lg">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="font-semibold text-lg">Hours spent cleaning per visit</label>
                  <span className="text-2xl font-bold text-primary">{hoursPerClean} hrs</span>
                </div>
                <Slider
                  value={[hoursPerClean]}
                  onValueChange={(val) => setHoursPerClean(val[0])}
                  max={10}
                  min={1}
                  step={0.5}
                  className="py-4"
                  aria-label="Hours spent cleaning"
                />
              </div>

              <div className="space-y-4">
                <label className="font-semibold text-lg block">How often do you clean?</label>
                <div className="grid grid-cols-3 gap-3">
                  {(["weekly", "biweekly", "monthly"] as const).map((freq) => (
                    <button
                      key={freq}
                      onClick={() => setFrequency(freq)}
                      className={cn(
                        "py-3 px-4 rounded-xl border-2 transition-all font-medium capitalize",
                        frequency === freq
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/30"
                      )}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <div className="space-y-6">
            <Card className="p-8 bg-primary text-primary-foreground border-none shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <Clock className="w-32 h-32" />
              </div>
              <div className="relative z-10">
                <p className="text-primary-foreground font-medium mb-2 text-lg">You will save</p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-6xl font-black text-primary-foreground">{hoursSavedYear}</span>
                  <span className="text-2xl font-semibold text-primary-foreground">hours per year</span>
                </div>
                <p className="text-xl text-primary-foreground flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary-foreground" />
                  That's <strong className="text-white">{daysSavedYear} full days</strong> of your life back!
                </p>
              </div>
            </Card>

            <Card className="p-6 border bg-card">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-primary" /> What could you do with that time?
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Spend more time with family
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Pick up a new hobby
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Finally relax on the weekends
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
